"use client";

import React from "react";
import { cn } from "@/lib/utils";

/**
 * CButtonGroup - A button group select component for single value selection
 *
 * Used for selecting one option from a set of mutually exclusive choices,
 * such as Business Type (Sole Trader | Partnership | LTD).
 *
 * @example
 * <CButtonGroup
 *   name="businessType"
 *   label="Business Type"
 *   options={[
 *     { label: "Sole Trader", value: "sole_trader" },
 *     { label: "Partnership", value: "partnership" },
 *     { label: "LTD", value: "ltd" },
 *   ]}
 *   value={selectedType}
 *   onChange={(value) => setSelectedType(value)}
 * />
 */

export interface CButtonGroupOption {
  /** Display label for the option */
  label: string;
  /** Value associated with the option */
  value: string;
}

export interface CButtonGroupProps {
  /** Name attribute for the input group */
  name: string;
  /** Label displayed above the button group */
  label?: React.ReactNode;
  /** Array of options to display as buttons */
  options: CButtonGroupOption[];
  /** Currently selected value */
  value?: string;
  /** Callback when selection changes */
  onChange?: (value: string) => void;
  /** Whether the button group is disabled */
  disabled?: boolean;
  /** Whether a selection is required */
  required?: boolean;
  /** Array of error messages (from form validation) */
  error?: string[];
  /** Whether to show error styling */
  errorQuery?: boolean;
  /** Error message to display */
  errorMessage?: string;
  /** Size variant */
  size?: "sm" | "default" | "lg";
  /** Whether buttons should fill the container width */
  fullWidth?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
}

const CButtonGroup: React.FC<CButtonGroupProps> = ({
  name,
  label,
  options,
  value,
  onChange,
  disabled = false,
  required = false,
  error,
  errorQuery = false,
  errorMessage = "Please select an option",
  size = "default",
  fullWidth = false,
  className,
}) => {
  // Derive error state from error array or errorQuery prop
  const hasError = errorQuery || (error && error.length > 0);
  const displayError = error?.[0] || errorMessage;

  // Size classes for padding
  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    default: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
  };

  const handleSelect = (optionValue: string) => {
    if (disabled) return;
    onChange?.(optionValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent, optionValue: string) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect(optionValue);
    }
  };

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <label
          htmlFor={name}
          className="text-sm text-muted-foreground mb-1 block"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div
        id={name}
        role="radiogroup"
        aria-label={typeof label === "string" ? label : name}
        aria-required={required}
        aria-invalid={hasError}
        className={cn("inline-flex flex-wrap gap-2", fullWidth && "w-full")}
      >
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => handleSelect(option.value)}
              onKeyDown={(e) => handleKeyDown(e, option.value)}
              className={cn(
                // Base styles
                "rounded-md border font-medium transition-all duration-200 cursor-pointer",
                "focus:outline-none",
                sizeClasses[size],
                fullWidth && "flex-1",
                // Default (unselected) state
                !isSelected && [
                  "border-slate-200 text-slate-600",
                  "hover:border-violet-300 hover:text-violet-600",
                  "dark:border-slate-700 dark:text-slate-400",
                  "dark:hover:border-violet-500 dark:hover:text-violet-400",
                ],
                // Selected state
                isSelected && [
                  "bg-primary/10 border-primary text-primary",
                  "dark:bg-primary/20 dark:border-primary dark:text-primary",
                ],
                // Error state
                hasError &&
                  !isSelected && [
                    "border-red-300 text-red-600",
                    "dark:border-red-500 dark:text-red-400",
                  ],
                // Disabled state
                disabled && [
                  "opacity-50 cursor-not-allowed",
                  "hover:border-slate-200 hover:text-slate-600",
                  "dark:hover:border-slate-700 dark:hover:text-slate-400",
                ],
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {hasError && (
        <p className="text-red-500 text-xs mt-1" role="alert">
          {displayError}
        </p>
      )}
    </div>
  );
};

export default CButtonGroup;
